import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Truck, MapPin, Package, FileText, Utensils, 
  ShoppingBag, Armchair, Boxes, CreditCard, Calendar, Clock,
  Bike, Car, Container, Plus, Minus, Check, Info, Camera, Upload, Loader2, Sparkles,
  Navigation, AlertCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { AddressAutocomplete } from './AddressAutocomplete';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPostalCode = (value) => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 7);
  return digits.length > 4 ? `${digits.slice(0, 4)}-${digits.slice(4)}` : digits;
};

const coordsOf = (address) => (
  typeof address.latitude === 'number' && typeof address.longitude === 'number'
    ? { latitude: address.latitude, longitude: address.longitude }
    : null
);

const addressLabel = (address) => (coordsOf(address)
  ? [[address.street, address.number].filter(Boolean).join(' '), address.neighborhood, `${address.zip_code} ${address.city}`.trim()].filter(Boolean).join(', ')
  : '');

const vehicleFits = (vehicle, analysis) => {
  if (!vehicle || !analysis) return true;
  const volumeOk = vehicle.max_volume_m3 == null || (analysis.volume_m3 ?? 0) <= vehicle.max_volume_m3;
  return volumeOk && (analysis.estimated_weight?.value ?? 0) <= vehicle.max_weight_kg;
};

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const categoryIcons = {
  Utensils: Utensils,
  ShoppingBag: ShoppingBag,
  Package: Package,
  FileText: FileText,
  Armchair: Armchair,
  Boxes: Boxes,
  Truck: Truck
};

const vehicleIcons = {
  Bike: Bike,
  Car: Car,
  Truck: Truck,
  Container: Container
};

const timeSlots = [
  { id: 'morning', label: 'Manhã', time: '08:00 - 12:00' },
  { id: 'afternoon', label: 'Tarde', time: '12:00 - 18:00' },
  { id: 'evening', label: 'Noite', time: '18:00 - 22:00' },
  { id: 'night', label: 'Madrugada', time: '22:00 - 08:00' }
];

export const NewOrderForm = () => {
  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [weights, setWeights] = useState([]);
  const [extras, setExtras] = useState([]);
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    description: '',
    vehicle_type: 'carro',
    weight_category: 'light',
    distance_km: 5,
    selected_extras: [],
    order_type: 'delivery',
    rental_hours: 2,
    pickup_address: {
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      zip_code: '',
      floor: 0,
      has_elevator: true
    },
    delivery_address: {
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      zip_code: '',
      floor: 0,
      has_elevator: true
    },
    recipient_name: '',
    recipient_phone: '',
    is_scheduled: false,
    scheduled_date: '',
    scheduled_time_slot: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [analyzingImage, setAnalyzingImage] = useState(false);
  const [imageAnalysis, setImageAnalysis] = useState(null);
  const [calculatingDistance, setCalculatingDistance] = useState(false);
  const [distanceInfo, setDistanceInfo] = useState(null); // { distance_km, duration_minutes }
  const [distanceError, setDistanceError] = useState('');
  const [vehicleComparison, setVehicleComparison] = useState([]); // [{vehicle_type, vehicle_name, total, ...}]
  const [pickupAddressInfo, setPickupAddressInfo] = useState(null); // {street, locality, region, label}
  const [deliveryAddressInfo, setDeliveryAddressInfo] = useState(null);
  const { token } = useAuth();
  const navigate = useNavigate();

  const fetchPricingData = useCallback(async () => {
    try {
      const [categoriesRes, vehiclesRes, weightsRes, extrasRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/pricing/vehicles`),
        axios.get(`${API}/pricing/weights`),
        axios.get(`${API}/pricing/extras`)
      ]);
      setCategories(categoriesRes.data);
      setVehicles(vehiclesRes.data);
      setWeights(weightsRes.data);
      setExtras(extrasRes.data);
    } catch (error) {
      console.error('Error fetching pricing data:', error);
    }
  }, []);

  // Fetch static pricing reference data once on mount
  useEffect(() => {
    fetchPricingData();
  }, [fetchPricingData]);

  const calculatePrice = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/pricing/calculate`, {
        vehicle_type: formData.vehicle_type,
        weight_category: formData.weight_category,
        distance_km: formData.distance_km,
        selected_extras: formData.selected_extras,
        pickup_floor: formData.pickup_address.floor,
        pickup_has_elevator: formData.pickup_address.has_elevator,
        delivery_floor: formData.delivery_address.floor,
        delivery_has_elevator: formData.delivery_address.has_elevator,
        is_scheduled: formData.is_scheduled && !!formData.scheduled_date,
        order_type: formData.order_type,
        rental_hours: formData.order_type === 'hourly' ? formData.rental_hours : 0
      });
      setPriceBreakdown(response.data);
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  }, [formData]);

  // Recompute price whenever any pricing-relevant input changes.
  // calculatePrice already depends on `formData`, so this single dependency
  // captures every relevant change without listing 12 individual paths.
  useEffect(() => {
    if (formData.vehicle_type && formData.weight_category) {
      calculatePrice();
    }
  }, [calculatePrice, formData.vehicle_type, formData.weight_category]);

  // Validate PT postal code format XXXX-XXX
  const isValidPTPostalCode = (zip) => /^\d{4}-\d{3}$/.test((zip || '').trim());

  const pickupZip = formData.pickup_address.zip_code;
  const deliveryZip = formData.delivery_address.zip_code;
  const pickupCoords = coordsOf(formData.pickup_address);
  const deliveryCoords = coordsOf(formData.delivery_address);
  const pickupKey = pickupCoords ? `${pickupCoords.latitude},${pickupCoords.longitude}` : pickupZip;
  const deliveryKey = deliveryCoords ? `${deliveryCoords.latitude},${deliveryCoords.longitude}` : deliveryZip;
  const hasRouteInput = (!!pickupCoords || isValidPTPostalCode(pickupZip)) && (!!deliveryCoords || isValidPTPostalCode(deliveryZip));

  // Debounced auto-calculation of distance using OpenRouteService
  useEffect(() => {
    if (!hasRouteInput) {
      setDistanceError('');
      setDistanceInfo(null);
      setVehicleComparison([]);
      return;
    }
    if (formData.order_type === 'hourly') {
      // Hourly mode: distance not relevant for price
      setDistanceError('');
      return;
    }

    let cancelled = false;
    setCalculatingDistance(true);
    setDistanceError('');

    const timer = setTimeout(async () => {
      try {
        const payload = {
          pickup_postal_code: pickupZip.trim(),
          delivery_postal_code: deliveryZip.trim(),
          pickup_coordinates: pickupCoords,
          delivery_coordinates: deliveryCoords,
          vehicle_type: formData.vehicle_type,
          weight_category: formData.weight_category,
          selected_extras: formData.selected_extras,
          pickup_floor: formData.pickup_address.floor,
          delivery_floor: formData.delivery_address.floor,
          pickup_has_elevator: formData.pickup_address.has_elevator,
          delivery_has_elevator: formData.delivery_address.has_elevator,
          is_scheduled: formData.is_scheduled && !!formData.scheduled_date,
          order_type: formData.order_type,
          rental_hours: formData.order_type === 'hourly' ? formData.rental_hours : 0
        };

        const [res, cmp] = await Promise.all([
          axios.post(`${API}/calculate-distance`, payload),
          axios.post(`${API}/calculate-distance/compare`, payload).catch(() => ({ data: { vehicles: [] } })),
        ]);

        if (cancelled) return;

        const km = res.data?.distance_km;
        const minutes = res.data?.duration_minutes;
        if (typeof km === 'number') {
          setDistanceInfo({ distance_km: km, duration_minutes: minutes });
          // Update formData.distance_km so calculatePrice picks it up
          setFormData(prev => ({ ...prev, distance_km: km }));
        } else {
          setDistanceError('Não foi possível calcular a distância. Verifique os códigos postais.');
        }

        setVehicleComparison(cmp.data?.vehicles || []);

        // Capture address info from the same call (no extra request)
        const pickupAddr = cmp.data?.pickup_address || res.data?.pickup_address || null;
        const deliveryAddr = cmp.data?.delivery_address || res.data?.delivery_address || null;
        setPickupAddressInfo(pickupAddr);
        setDeliveryAddressInfo(deliveryAddr);

        // Pre-fill empty address fields so Step 3 only needs número + andar
        setFormData(prev => {
          const next = { ...prev };
          if (pickupAddr) {
            next.pickup_address = {
              ...prev.pickup_address,
              street: prev.pickup_address.street || pickupAddr.street || '',
              neighborhood: prev.pickup_address.neighborhood || pickupAddr.neighbourhood || '',
              city: prev.pickup_address.city || pickupAddr.locality || '',
              state: prev.pickup_address.state || pickupAddr.region || '',
            };
          }
          if (deliveryAddr) {
            next.delivery_address = {
              ...prev.delivery_address,
              street: prev.delivery_address.street || deliveryAddr.street || '',
              neighborhood: prev.delivery_address.neighborhood || deliveryAddr.neighbourhood || '',
              city: prev.delivery_address.city || deliveryAddr.locality || '',
              state: prev.delivery_address.state || deliveryAddr.region || '',
            };
          }
          return next;
        });
      } catch (err) {
        if (cancelled) return;
        const detail = err.response?.data?.detail || 'Erro ao calcular distância. Verifique as moradas ou códigos postais.';
        setDistanceError(detail);
        setDistanceInfo(null);
        setVehicleComparison([]);
        setPickupAddressInfo(null);
        setDeliveryAddressInfo(null);
      } finally {
        if (!cancelled) setCalculatingDistance(false);
      }
    }, 600); // debounce 600ms

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pickupKey, deliveryKey, hasRouteInput, formData.vehicle_type, formData.order_type]);

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 30);
    return maxDate.toISOString().split('T')[0];
  };

  // Memoize filtered vehicles to avoid re-computation on every render
  const filteredVehicles = useMemo(() => {
    return vehicles.filter(vehicle => 
      formData.order_type === 'delivery' || 
      (formData.order_type === 'hourly' && (vehicle.id === 'mota' || vehicle.id === 'carro'))
    );
  }, [vehicles, formData.order_type]);

  const handleAddressChange = (type, field, value) => {
    if (field === 'zip_code' || field === 'street') {
      setFormData(prev => ({
        ...prev,
        [type]: {
          ...prev[type],
          [field]: field === 'zip_code' ? formatPostalCode(value) : value,
          latitude: null,
          longitude: null
        }
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
  };

  const [locating, setLocating] = useState(false);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      setError('Este dispositivo não permite obter a localização.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(async ({ coords }) => {
      try {
        const res = await axios.get(`${API}/address-reverse`, { params: { lat: coords.latitude, lon: coords.longitude } });
        handleAddressSelect('pickup_address', res.data);
        setError('');
      } catch (err) {
        setError(err.response?.data?.detail || 'Não foi possível encontrar a sua morada. Pesquise-a manualmente.');
      } finally {
        setLocating(false);
      }
    }, () => {
      setLocating(false);
      setError('Autorize o acesso à localização no navegador ou pesquise a morada manualmente.');
    }, { enableHighAccuracy: true, timeout: 15000 });
  };

  const handleAddressSelect = (type, item) => {
    setFormData(prev => {
      const current = prev[type];
      if (!item) return { ...prev, [type]: { ...current, latitude: null, longitude: null } };
      return {
        ...prev,
        [type]: {
          ...current,
          street: item.street || current.street,
          number: item.number || (item.street === current.street ? current.number : ''),
          neighborhood: item.neighborhood || current.neighborhood,
          city: item.city || current.city,
          state: item.state || current.state,
          zip_code: item.zip_code || current.zip_code,
          latitude: item.latitude,
          longitude: item.longitude,
        }
      };
    });
  };

  const toggleExtra = (extraId) => {
    setFormData(prev => ({
      ...prev,
      selected_extras: prev.selected_extras.includes(extraId)
        ? prev.selected_extras.filter(id => id !== extraId)
        : [...prev.selected_extras, extraId]
    }));
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      const orderData = {
        category: formData.category,
        description: formData.description,
        vehicle_type: formData.vehicle_type,
        weight_category: formData.weight_category,
        distance_km: formData.distance_km,
        selected_extras: formData.selected_extras,
        pickup_address: formData.pickup_address,
        delivery_address: formData.delivery_address,
        recipient_name: formData.recipient_name,
        recipient_phone: formData.recipient_phone,
        scheduled_date: formData.is_scheduled ? formData.scheduled_date : null,
        scheduled_time_slot: formData.is_scheduled ? formData.scheduled_time_slot : null,
        order_type: formData.order_type,
        rental_hours: formData.order_type === 'hourly' ? formData.rental_hours : 0
      };

      const response = await axios.post(`${API}/orders`, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      navigate(`/orders/${response.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao criar pedido');
    } finally {
      setLoading(false);
    }
  };

  const analyzeImage = async (file) => {
    setAnalyzingImage(true);
    setError('');
    
    try {
      const base64Image = await readFileAsDataUrl(file);
      const response = await axios.post(`${API}/ai/analyze-object`, {
        image: base64Image
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const analysis = response.data.analysis;
      setImageAnalysis(analysis);
      setFormData(prev => ({
        ...prev,
        vehicle_type: analysis.recommended_vehicle || prev.vehicle_type,
        weight_category: analysis.estimated_weight?.category || prev.weight_category,
        description: prev.description || analysis.object_type || '',
      }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao analisar imagem. Tente novamente.');
      console.error('Image analysis error:', err);
    } finally {
      setAnalyzingImage(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setError('Imagem muito grande. Máximo 10MB.');
        return;
      }
      analyzeImage(file);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-gray-600 hover:text-tuggo-mint mb-6">
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </Link>

        <div className="bg-white rounded-3xl p-8 shadow-xl">
          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-8">
            {[1, 2, 3, 4, 5].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step >= s ? 'bg-tuggo-mint text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {s}
                </div>
                {s < 5 && (
                  <div className={`w-8 md:w-16 h-1 mx-1 ${
                    step > s ? 'bg-tuggo-mint' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl mb-6">
              {error}
            </div>
          )}

          {/* Persistent Estimated Price Banner (visible from Step 2 onwards) */}
          {step >= 2 && (
            <div className="mb-6" data-testid="estimated-price-banner">
              <div className="bg-gradient-to-r from-tuggo-mint/15 to-tuggo-mint/5 border border-tuggo-mint/30 rounded-2xl p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-tuggo-mint/20 flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-tuggo-mint" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500">Preço estimado</div>
                      {priceBreakdown ? (
                        <div className="text-2xl font-bold text-tuggo-mint" data-testid="estimated-price-value">
                          €{priceBreakdown.total.toFixed(2)}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">A calcular...</div>
                      )}
                    </div>
                  </div>
                  <div className="text-right text-xs text-gray-600 space-y-0.5">
                    {distanceInfo && formData.order_type !== 'hourly' && (
                      <div className="flex items-center justify-end gap-1">
                        <MapPin className="w-3 h-3" />
                        <span>{distanceInfo.distance_km} km</span>
                      </div>
                    )}
                    {distanceInfo?.duration_minutes != null && formData.order_type !== 'hourly' && (
                      <div className="flex items-center justify-end gap-1">
                        <Clock className="w-3 h-3" />
                        <span>~{Math.round(distanceInfo.duration_minutes)} min</span>
                      </div>
                    )}
                    {formData.order_type === 'hourly' && (
                      <div className="flex items-center justify-end gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{formData.rental_hours}h</span>
                      </div>
                    )}
                    {calculatingDistance && (
                      <div className="flex items-center justify-end gap-1 text-tuggo-mint">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        <span>a calcular</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Category & Vehicle */}
          {step === 1 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">O que vai entregar?</h2>
              <p className="text-gray-600 mb-6">Tire uma foto ou selecione manualmente</p>

              {/* AI Image Analyzer */}
              <div className="mb-6 bg-gradient-to-br from-tuggo-mint/10 to-blue-50 rounded-2xl p-6 border-2 border-dashed border-tuggo-mint/30">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 bg-tuggo-mint rounded-xl flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">Assistente Inteligente</h3>
                    <p className="text-sm text-gray-600">Tire uma foto do objeto e deixamos a IA sugerir o veículo ideal e estimar o preço</p>
                  </div>
                </div>

                <label className="block">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleImageUpload}
                    disabled={analyzingImage}
                    className="hidden"
                    id="image-upload"
                  />
                  <div className={`border-2 border-tuggo-mint/50 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all hover:border-tuggo-mint hover:bg-white/50 ${
                    analyzingImage ? 'opacity-50 cursor-wait' : ''
                  }`}>
                    {analyzingImage ? (
                      <div className="flex flex-col items-center">
                        <Loader2 className="w-8 h-8 text-tuggo-mint animate-spin mb-2" />
                        <p className="text-sm text-gray-600">A analisar imagem...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <Camera className="w-8 h-8 text-tuggo-mint mb-2" />
                        <p className="text-sm font-medium text-gray-900">Tirar Foto / Carregar Imagem</p>
                        <p className="text-xs text-gray-500 mt-1">PNG, JPG até 10MB</p>
                      </div>
                    )}
                  </div>
                </label>

                {imageAnalysis && (
                  <div className="mt-4 bg-white rounded-xl p-4 space-y-2" data-testid="ai-analysis-result">
                    <div className="flex items-center gap-2 text-green-600 mb-2">
                      <Check className="w-5 h-5" />
                      <span className="font-semibold">Análise concluída!</span>
                    </div>
                    {imageAnalysis.object_type && (
                      <p className="text-sm text-gray-700" data-testid="ai-object-type">
                        <strong>Objeto:</strong> {imageAnalysis.object_type}
                        {imageAnalysis.quantity > 1 && ` (x${imageAnalysis.quantity})`}
                      </p>
                    )}
                    {imageAnalysis.dimensions && (
                      <p className="text-sm text-gray-700" data-testid="ai-dimensions">
                        <strong>Medidas:</strong> {imageAnalysis.dimensions.length} × {imageAnalysis.dimensions.width} × {imageAnalysis.dimensions.height} cm
                      </p>
                    )}
                    {imageAnalysis.volume_m3 != null && (
                      <p className="text-sm text-gray-700" data-testid="ai-volume">
                        <strong>Volume (cubagem):</strong> {String(imageAnalysis.volume_m3).replace('.', ',')} m³
                      </p>
                    )}
                    {imageAnalysis.estimated_weight && (
                      <p className="text-sm text-gray-700" data-testid="ai-weight">
                        <strong>Peso estimado:</strong> {imageAnalysis.estimated_weight.value} {imageAnalysis.estimated_weight.unit}
                      </p>
                    )}
                    {imageAnalysis.recommended_vehicle && (
                      <div className="bg-tuggo-mint/10 rounded-lg px-3 py-2" data-testid="ai-recommended-vehicle">
                        <p className="text-sm text-gray-900">
                          <strong>Veículo sugerido:</strong> {imageAnalysis.recommended_vehicle_name || imageAnalysis.recommended_vehicle}
                        </p>
                        {imageAnalysis.recommendation_reason && (
                          <p className="text-xs text-gray-600 mt-0.5">{imageAnalysis.recommendation_reason}</p>
                        )}
                      </div>
                    )}
                    {imageAnalysis.handling_notes && (
                      <p className="text-xs text-gray-500">{imageAnalysis.handling_notes}</p>
                    )}
                    {imageAnalysis.price_estimate && (
                      <p className="text-sm text-tuggo-mint font-semibold" data-testid="ai-price-estimate">
                        <strong>Estimativa (5-15 km):</strong> €{imageAnalysis.price_estimate.min.toFixed(2)} - €{imageAnalysis.price_estimate.max.toFixed(2)}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="mb-6">
                <Label className="text-base font-semibold mb-3 block">Categoria</Label>
                <div className="grid grid-cols-2 gap-3">
                  {categories.map((cat) => {
                    const IconComponent = categoryIcons[cat.icon] || Package;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => setFormData({ ...formData, category: cat.id })}
                        className={`p-3 rounded-xl border-2 transition-all text-left ${
                          formData.category === cat.id
                            ? 'border-tuggo-mint bg-tuggo-mint/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`category-${cat.id}`}
                      >
                        <IconComponent className={`w-6 h-6 mb-1 ${
                          formData.category === cat.id ? 'text-tuggo-mint' : 'text-gray-400'
                        }`} />
                        <div className="font-semibold text-gray-900 text-sm">{cat.name}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Service Type Selection */}
              <div className="mb-6">
                <Label className="text-base font-semibold mb-3 block">Tipo de Serviço</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setFormData({ ...formData, order_type: 'delivery' })}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      formData.order_type === 'delivery'
                        ? 'border-tuggo-mint bg-tuggo-mint/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="service-delivery"
                  >
                    <Package className={`w-8 h-8 mb-2 ${
                      formData.order_type === 'delivery' ? 'text-tuggo-mint' : 'text-gray-400'
                    }`} />
                    <div className="font-semibold text-gray-900">Entrega Normal</div>
                    <div className="text-xs text-gray-500 mt-1">Transporte de encomenda de A para B</div>
                  </button>
                  <button
                    onClick={() => setFormData({ ...formData, order_type: 'hourly' })}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      formData.order_type === 'hourly'
                        ? 'border-tuggo-mint bg-tuggo-mint/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="service-hourly"
                  >
                    <Clock className={`w-8 h-8 mb-2 ${
                      formData.order_type === 'hourly' ? 'text-tuggo-mint' : 'text-gray-400'
                    }`} />
                    <div className="font-semibold text-gray-900">Estafeta por Hora</div>
                    <div className="text-xs text-gray-500 mt-1">Estafeta dedicado (mota/carro)</div>
                  </button>
                </div>
              </div>

              {/* Hourly Duration Selector (only for hourly service) */}
              {formData.order_type === 'hourly' && (
                <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <Label className="text-base font-semibold mb-3 block flex items-center gap-2">
                    <Clock className="w-5 h-5 text-tuggo-mint" />
                    Quantas horas precisa?
                  </Label>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setFormData({ ...formData, rental_hours: Math.max(1, formData.rental_hours - 1) })}
                      className="w-10 h-10 rounded-full bg-white border-2 border-tuggo-mint text-tuggo-mint font-bold hover:bg-tuggo-mint hover:text-white transition-all"
                    >
                      <Minus className="w-5 h-5 mx-auto" />
                    </button>
                    <div className="flex-1 text-center">
                      <div className="text-4xl font-bold text-tuggo-mint">{formData.rental_hours}</div>
                      <div className="text-sm text-gray-600">{formData.rental_hours === 1 ? 'hora' : 'horas'}</div>
                    </div>
                    <button
                      onClick={() => setFormData({ ...formData, rental_hours: Math.min(8, formData.rental_hours + 1) })}
                      className="w-10 h-10 rounded-full bg-white border-2 border-tuggo-mint text-tuggo-mint font-bold hover:bg-tuggo-mint hover:text-white transition-all"
                    >
                      <Plus className="w-5 h-5 mx-auto" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-600 text-center mt-3">
                    <Info className="w-3 h-3 inline mr-1" />
                    Ideal para restaurantes e fast-foods. Mínimo: 1h | Máximo: 8h
                  </p>
                </div>
              )}

              <div className="mb-6">
                <Label className="text-base font-semibold mb-3 block">Veículo</Label>
                {formData.order_type === 'hourly' && (
                  <p className="text-sm text-amber-600 mb-3 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Serviço por hora disponível apenas para Mota e Carro
                  </p>
                )}
                <div className="grid grid-cols-2 gap-3">
                  {filteredVehicles.map((vehicle) => {
                    const IconComponent = vehicleIcons[vehicle.icon] || Car;
                    return (
                      <button
                        key={vehicle.id}
                        onClick={() => setFormData({ ...formData, vehicle_type: vehicle.id })}
                        className={`p-4 rounded-xl border-2 transition-all text-left ${
                          formData.vehicle_type === vehicle.id
                            ? 'border-tuggo-mint bg-tuggo-mint/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`vehicle-${vehicle.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <IconComponent className={`w-8 h-8 ${
                            formData.vehicle_type === vehicle.id ? 'text-tuggo-mint' : 'text-gray-400'
                          }`} />
                          <span className="text-tuggo-mint font-bold">€{vehicle.base_price.toFixed(2)}</span>
                        </div>
                        <div className="font-semibold text-gray-900">{vehicle.name}</div>
                        <div className="text-xs text-gray-500">{vehicle.description}</div>
                        <div className="text-xs text-tuggo-mint mt-1">+€{vehicle.price_per_km.toFixed(2)}/km</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label>Descrição do item</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Ex: Caixa com documentos, Sofá de 3 lugares..."
                  className="mt-2"
                  data-testid="order-description"
                />
              </div>

              <Button
                onClick={() => setStep(2)}
                disabled={!formData.category || !formData.description}
                className="w-full mt-6 bg-tuggo-mint text-white rounded-full py-6"
                data-testid="next-step-1"
              >
                Continuar
              </Button>
            </div>
          )}

          {/* Step 2: Weight & Distance */}
          {step === 2 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Peso e Distância</h2>
              <p className="text-gray-600 mb-6">Indique o peso estimado e as moradas de recolha e entrega</p>

              <div className="mb-6">
                <Label className="text-base font-semibold mb-3 block">Peso Estimado</Label>
                <div className="grid grid-cols-2 gap-3">
                  {weights.map((weight) => (
                    <button
                      key={weight.id}
                      onClick={() => setFormData({ ...formData, weight_category: weight.id })}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        formData.weight_category === weight.id
                          ? 'border-tuggo-mint bg-tuggo-mint/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`weight-${weight.id}`}
                    >
                      <div className="font-semibold text-gray-900">{weight.name}</div>
                      <div className="text-sm text-gray-500">{weight.description}</div>
                      {weight.multiplier > 1 && (
                        <div className="text-xs text-tuggo-mint mt-1">+{((weight.multiplier - 1) * 100).toFixed(0)}% no preço</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Postal Code Distance Calculator */}
              <div className="mb-6">
                <Label className="text-base font-semibold mb-3 block flex items-center gap-2">
                  <Navigation className="w-5 h-5 text-tuggo-mint" />
                  Moradas (cálculo automático de distância)
                </Label>
                <div className="space-y-3">
                  <AddressAutocomplete
                    label="Morada de Recolha"
                    value={addressLabel(formData.pickup_address)}
                    placeholder="Pesquise rua, número e cidade (ex: Rua Augusta 100 Lisboa)"
                    selected={!!pickupCoords}
                    onSelect={(item) => handleAddressSelect('pickup_address', item)}
                    testId="pickup-address-search"
                    bias={deliveryCoords}
                  />
                  <button
                    type="button"
                    onClick={useMyLocation}
                    disabled={locating}
                    className="flex items-center gap-2 text-sm font-medium text-tuggo-mint hover:underline disabled:opacity-60"
                    data-testid="use-my-location-btn"
                  >
                    {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                    {locating ? 'A obter a sua localização...' : 'Usar a minha localização atual'}
                  </button>
                  <div>
                    <Label className="text-sm text-gray-600">Código Postal de Recolha</Label>
                    <Input
                      value={formData.pickup_address.zip_code}
                      onChange={(e) => handleAddressChange('pickup_address', 'zip_code', e.target.value)}
                      placeholder="Ex: 1000-001"
                      maxLength="8"
                      className="mt-1"
                      data-testid="step2-pickup-zip"
                    />
                    {pickupAddressInfo?.locality && (
                      <div className="mt-1.5 text-xs text-gray-600 flex items-center gap-1" data-testid="step2-pickup-locality">
                        <MapPin className="w-3 h-3 text-tuggo-mint" />
                        <span className="font-medium text-gray-800">{pickupAddressInfo.locality}</span>
                        {pickupAddressInfo.region && pickupAddressInfo.region !== pickupAddressInfo.locality && (
                          <span className="text-gray-500">· {pickupAddressInfo.region}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <AddressAutocomplete
                    label="Morada de Entrega"
                    value={addressLabel(formData.delivery_address)}
                    placeholder="Pesquise rua, número e cidade (ex: Rua de Santa Catarina 300 Porto)"
                    selected={!!deliveryCoords}
                    onSelect={(item) => handleAddressSelect('delivery_address', item)}
                    testId="delivery-address-search"
                    bias={pickupCoords}
                  />
                  <div>
                    <Label className="text-sm text-gray-600">Código Postal de Entrega</Label>
                    <Input
                      value={formData.delivery_address.zip_code}
                      onChange={(e) => handleAddressChange('delivery_address', 'zip_code', e.target.value)}
                      placeholder="Ex: 4000-001"
                      maxLength="8"
                      className="mt-1"
                      data-testid="step2-delivery-zip"
                    />
                    {deliveryAddressInfo?.locality && (
                      <div className="mt-1.5 text-xs text-gray-600 flex items-center gap-1" data-testid="step2-delivery-locality">
                        <MapPin className="w-3 h-3 text-tuggo-mint" />
                        <span className="font-medium text-gray-800">{deliveryAddressInfo.locality}</span>
                        {deliveryAddressInfo.region && deliveryAddressInfo.region !== deliveryAddressInfo.locality && (
                          <span className="text-gray-500">· {deliveryAddressInfo.region}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Distance status */}
                {formData.order_type !== 'hourly' && (
                  <div className="mt-3">
                    {calculatingDistance && (
                      <div className="flex items-center gap-2 text-gray-600 text-sm" data-testid="distance-loading">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        A calcular distância...
                      </div>
                    )}
                    {!calculatingDistance && distanceError && (
                      <div className="flex items-start gap-2 text-red-600 text-sm bg-red-50 rounded-lg p-3" data-testid="distance-error">
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{distanceError}</span>
                      </div>
                    )}
                    {!calculatingDistance && !distanceError && distanceInfo && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm" data-testid="distance-result">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-700 flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-green-600" />
                            Distância de condução
                          </span>
                          <span className="font-bold text-gray-900">{distanceInfo.distance_km} km</span>
                        </div>
                        {distanceInfo.duration_minutes != null && (
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-gray-700 flex items-center gap-2">
                              <Clock className="w-4 h-4 text-green-600" />
                              Tempo estimado
                            </span>
                            <span className="text-gray-700">~{Math.round(distanceInfo.duration_minutes)} min</span>
                          </div>
                        )}
                      </div>
                    )}
                    {!calculatingDistance && !distanceError && !distanceInfo && (
                      <p className="text-xs text-gray-500 mt-2">
                        <Info className="w-3 h-3 inline mr-1" />
                        Selecione as duas moradas da lista (ou indique os códigos postais XXXX-XXX) para calcular automaticamente a distância e o preço.
                      </p>
                    )}
                  </div>
                )}
                {formData.order_type === 'hourly' && (
                  <p className="text-xs text-gray-500 mt-2">
                    <Info className="w-3 h-3 inline mr-1" />
                    Em serviço por hora, a distância não influencia o preço.
                  </p>
                )}
              </div>

              {/* Vehicle Comparison Cards */}
              {vehicleComparison.length > 0 && !calculatingDistance && !distanceError && (
                <div className="mb-6" data-testid="vehicle-comparison">
                  <Label className="text-base font-semibold mb-3 block flex items-center gap-2">
                    <Truck className="w-5 h-5 text-tuggo-mint" />
                    Compare e poupe — escolha o veículo
                  </Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {vehicleComparison.map((v) => {
                      const vehicleMeta = vehicles.find(vv => vv.id === v.vehicle_type);
                      const IconComponent = vehicleIcons[vehicleMeta?.icon] || Car;
                      const isSelected = formData.vehicle_type === v.vehicle_type;
                      const fits = vehicleFits(vehicleMeta, imageAnalysis);
                      const cheapestFitting = vehicleComparison.find(c => vehicleFits(vehicles.find(vv => vv.id === c.vehicle_type), imageAnalysis));
                      const isCheapest = fits && cheapestFitting?.vehicle_type === v.vehicle_type;
                      const cheapestTotal = cheapestFitting?.total ?? v.total;
                      const savingsVsSelected = v.total - cheapestTotal;
                      return (
                        <button
                          key={v.vehicle_type}
                          onClick={() => setFormData(prev => ({ ...prev, vehicle_type: v.vehicle_type }))}
                          className={`relative p-4 rounded-2xl border-2 transition-all text-left ${
                            isSelected
                              ? 'border-tuggo-mint bg-tuggo-mint/10 shadow-md'
                              : 'border-gray-200 bg-white hover:border-tuggo-mint/50'
                          } ${fits ? '' : 'opacity-50'}`}
                          data-testid={`compare-vehicle-${v.vehicle_type}`}
                        >
                          {isCheapest && (
                            <span className="absolute -top-2 -right-2 bg-tuggo-mint text-white text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full shadow">
                              Mais barato
                            </span>
                          )}
                          <div className="flex items-center justify-between mb-2">
                            <IconComponent className={`w-7 h-7 ${isSelected ? 'text-tuggo-mint' : 'text-gray-500'}`} />
                            {isSelected && <Check className="w-5 h-5 text-tuggo-mint" />}
                          </div>
                          <div className="font-semibold text-gray-900">{v.vehicle_name}</div>
                          {vehicleMeta && (
                            <div className="text-[11px] text-gray-500">
                              {vehicleMeta.max_volume_m3 ? `até ${String(vehicleMeta.max_volume_m3).replace('.', ',')} m³` : '+18 m³'} · {vehicleMeta.max_weight_kg} kg
                            </div>
                          )}
                          {imageAnalysis?.recommended_vehicle === v.vehicle_type && (
                            <div className="text-[11px] font-semibold text-blue-600 mt-0.5" data-testid={`ai-suggested-${v.vehicle_type}`}>Sugerido pela IA</div>
                          )}
                          <div className="mt-2 text-2xl font-bold text-tuggo-mint">€{v.total.toFixed(2)}</div>
                          {!fits && (
                            <div className="text-[11px] text-red-600 mt-1 font-medium" data-testid={`vehicle-too-small-${v.vehicle_type}`}>Pequeno demais para a carga estimada</div>
                          )}
                          {fits && !isCheapest && savingsVsSelected > 0 && (
                            <div className="text-[11px] text-gray-500 mt-1">+€{savingsVsSelected.toFixed(2)} vs mais barato</div>
                          )}
                          {isCheapest && vehicleComparison.length > 1 && (
                            <div className="text-[11px] text-tuggo-mint mt-1 font-medium">Melhor preço para este trajeto</div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    <Info className="w-3 h-3 inline mr-1" />
                    Verifique se o veículo escolhido suporta o peso e dimensões da sua encomenda.
                  </p>
                </div>
              )}

              <div className="flex gap-4">
                <Button onClick={() => setStep(1)} variant="outline" className="flex-1 rounded-full py-6">
                  Voltar
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={
                    formData.order_type !== 'hourly' && (
                      !hasRouteInput ||
                      calculatingDistance ||
                      !!distanceError ||
                      !distanceInfo
                    )
                  }
                  className="flex-1 bg-tuggo-mint text-white rounded-full py-6"
                  data-testid="next-step-2"
                >
                  Continuar
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Addresses */}
          {step === 3 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Moradas</h2>
              <p className="text-gray-600 mb-4">Complete os detalhes da recolha e entrega</p>

              {(pickupAddressInfo?.locality || deliveryAddressInfo?.locality) && (
                <div className="mb-6 bg-tuggo-mint/10 border border-tuggo-mint/30 rounded-xl px-4 py-3 text-sm flex items-start gap-2" data-testid="step3-prefill-notice">
                  <Info className="w-4 h-4 text-tuggo-mint mt-0.5 flex-shrink-0" />
                  <div className="text-gray-700">
                    Preenchemos a morada com base na sua seleção / código postal.
                    Confirme a <strong>Rua e o Número</strong> e indique o <strong>Andar</strong>.
                  </div>
                </div>
              )}

              {/* Pickup Address */}
              <div className="mb-6 p-4 bg-green-50 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 bg-green-500 rounded-full" />
                  <span className="font-semibold text-gray-900">Morada de Recolha</span>
                </div>
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <Input
                        value={formData.pickup_address.street}
                        onChange={(e) => handleAddressChange('pickup_address', 'street', e.target.value)}
                        placeholder="Rua"
                        data-testid="pickup-street"
                      />
                    </div>
                    <Input
                      value={formData.pickup_address.number}
                      onChange={(e) => handleAddressChange('pickup_address', 'number', e.target.value)}
                      placeholder="Nº"
                      data-testid="pickup-number"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      value={formData.pickup_address.neighborhood}
                      onChange={(e) => handleAddressChange('pickup_address', 'neighborhood', e.target.value)}
                      placeholder="Freguesia"
                      data-testid="pickup-neighborhood"
                    />
                    <Input
                      value={formData.pickup_address.city}
                      onChange={(e) => handleAddressChange('pickup_address', 'city', e.target.value)}
                      placeholder="Cidade"
                      data-testid="pickup-city"
                    />
                  </div>
                  <Input
                    value={formData.pickup_address.zip_code}
                    onChange={(e) => handleAddressChange('pickup_address', 'zip_code', e.target.value)}
                    placeholder="Código Postal (ex: 1000-001)"
                    maxLength="8"
                    data-testid="pickup-zipcode"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Andar</Label>
                      <Input
                        type="number"
                        value={formData.pickup_address.floor}
                        onChange={(e) => handleAddressChange('pickup_address', 'floor', parseInt(e.target.value) || 0)}
                        placeholder="0"
                        min="0"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Elevador?</Label>
                      <div className="flex gap-2 mt-1">
                        <button
                          onClick={() => handleAddressChange('pickup_address', 'has_elevator', true)}
                          className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                            formData.pickup_address.has_elevator
                              ? 'bg-tuggo-mint text-white'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          Sim
                        </button>
                        <button
                          onClick={() => handleAddressChange('pickup_address', 'has_elevator', false)}
                          className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                            !formData.pickup_address.has_elevator
                              ? 'bg-red-500 text-white'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          Não
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Delivery Address */}
              <div className="mb-6 p-4 bg-red-50 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 bg-red-500 rounded-full" />
                  <span className="font-semibold text-gray-900">Morada de Entrega</span>
                </div>
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <Input
                        value={formData.delivery_address.street}
                        onChange={(e) => handleAddressChange('delivery_address', 'street', e.target.value)}
                        placeholder="Rua"
                        data-testid="delivery-street"
                      />
                    </div>
                    <Input
                      value={formData.delivery_address.number}
                      onChange={(e) => handleAddressChange('delivery_address', 'number', e.target.value)}
                      placeholder="Nº"
                      data-testid="delivery-number"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      value={formData.delivery_address.neighborhood}
                      onChange={(e) => handleAddressChange('delivery_address', 'neighborhood', e.target.value)}
                      placeholder="Freguesia"
                      data-testid="delivery-neighborhood"
                    />
                    <Input
                      value={formData.delivery_address.city}
                      onChange={(e) => handleAddressChange('delivery_address', 'city', e.target.value)}
                      placeholder="Cidade"
                      data-testid="delivery-city"
                    />
                  </div>
                  <Input
                    value={formData.delivery_address.zip_code}
                    onChange={(e) => handleAddressChange('delivery_address', 'zip_code', e.target.value)}
                    placeholder="Código Postal (ex: 1000-001)"
                    maxLength="8"
                    data-testid="delivery-zipcode"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Andar</Label>
                      <Input
                        type="number"
                        value={formData.delivery_address.floor}
                        onChange={(e) => handleAddressChange('delivery_address', 'floor', parseInt(e.target.value) || 0)}
                        placeholder="0"
                        min="0"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Elevador?</Label>
                      <div className="flex gap-2 mt-1">
                        <button
                          onClick={() => handleAddressChange('delivery_address', 'has_elevator', true)}
                          className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                            formData.delivery_address.has_elevator
                              ? 'bg-tuggo-mint text-white'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          Sim
                        </button>
                        <button
                          onClick={() => handleAddressChange('delivery_address', 'has_elevator', false)}
                          className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                            !formData.delivery_address.has_elevator
                              ? 'bg-red-500 text-white'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          Não
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recipient */}
              <div className="p-4 bg-gray-50 rounded-xl">
                <Label className="font-semibold text-gray-900 mb-3 block">Destinatário</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    value={formData.recipient_name}
                    onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
                    placeholder="Nome"
                    data-testid="recipient-name"
                  />
                  <Input
                    value={formData.recipient_phone}
                    onChange={(e) => setFormData({ ...formData, recipient_phone: e.target.value })}
                    placeholder="Telemóvel"
                    data-testid="recipient-phone"
                  />
                </div>
              </div>

              <div className="flex gap-4 mt-6">
                <Button onClick={() => setStep(2)} variant="outline" className="flex-1 rounded-full py-6">
                  Voltar
                </Button>
                <Button
                  onClick={() => setStep(4)}
                  disabled={!formData.pickup_address.street || !formData.delivery_address.street || !formData.recipient_name}
                  className="flex-1 bg-tuggo-mint text-white rounded-full py-6"
                  data-testid="next-step-3"
                >
                  Continuar
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Extras & Scheduling */}
          {step === 4 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Serviços Extra</h2>
              <p className="text-gray-600 mb-6">Adicione serviços opcionais</p>

              <div className="space-y-3 mb-6">
                {extras.map((extra) => {
                  const isSelected = formData.selected_extras.includes(extra.id);
                  const isStairsService = extra.id.includes('stairs');
                  
                  // Show stairs services based on elevator status
                  if (extra.id === 'stairs_pickup' && formData.pickup_address.has_elevator) return null;
                  if (extra.id === 'stairs_delivery' && formData.delivery_address.has_elevator) return null;
                  
                  return (
                    <button
                      key={extra.id}
                      onClick={() => toggleExtra(extra.id)}
                      className={`w-full p-4 rounded-xl border-2 transition-all text-left flex items-center justify-between ${
                        isSelected
                          ? 'border-tuggo-mint bg-tuggo-mint/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`extra-${extra.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'border-tuggo-mint bg-tuggo-mint' : 'border-gray-300'
                        }`}>
                          {isSelected && <Check className="w-4 h-4 text-white" />}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{extra.name}</div>
                          <div className="text-sm text-gray-500">{extra.description}</div>
                        </div>
                      </div>
                      <div className="text-tuggo-mint font-bold">
                        +€{extra.price.toFixed(2)}
                        {isStairsService && '/andar'}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Scheduling */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-tuggo-mint" />
                    <span className="font-semibold text-gray-900">Agendar Entrega</span>
                  </div>
                  <button
                    onClick={() => setFormData({ ...formData, is_scheduled: !formData.is_scheduled })}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      formData.is_scheduled ? 'bg-tuggo-mint' : 'bg-gray-300'
                    }`}
                    data-testid="schedule-toggle"
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      formData.is_scheduled ? 'left-7' : 'left-1'
                    }`} />
                  </button>
                </div>

                {formData.is_scheduled && (
                  <div className="space-y-4 pt-4 border-t border-gray-200">
                    <div>
                      <Label>Data</Label>
                      <Input
                        type="date"
                        value={formData.scheduled_date}
                        onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                        min={getMinDate()}
                        max={getMaxDate()}
                        className="mt-2"
                        data-testid="schedule-date"
                      />
                    </div>
                    <div>
                      <Label>Horário</Label>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {timeSlots.map((slot) => (
                          <button
                            key={slot.id}
                            onClick={() => setFormData({ ...formData, scheduled_time_slot: slot.id })}
                            className={`p-2 rounded-xl border-2 text-left text-sm ${
                              formData.scheduled_time_slot === slot.id
                                ? 'border-tuggo-mint bg-tuggo-mint/5'
                                : 'border-gray-200'
                            }`}
                            data-testid={`time-${slot.id}`}
                          >
                            <div className="font-medium">{slot.label}</div>
                            <div className="text-xs text-gray-500">{slot.time}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="bg-tuggo-mint/10 rounded-lg p-3 flex items-center gap-2">
                      <Info className="w-4 h-4 text-tuggo-mint" />
                      <span className="text-sm text-tuggo-mint">+15% taxa de agendamento</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-4">
                <Button onClick={() => setStep(3)} variant="outline" className="flex-1 rounded-full py-6">
                  Voltar
                </Button>
                <Button
                  onClick={() => setStep(5)}
                  className="flex-1 bg-tuggo-mint text-white rounded-full py-6"
                  data-testid="next-step-4"
                >
                  Continuar
                </Button>
              </div>
            </div>
          )}

          {/* Step 5: Confirmation */}
          {step === 5 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Confirmar Pedido</h2>
              <p className="text-gray-600 mb-6">Revise os detalhes antes de confirmar</p>

              <div className="space-y-4">
                {/* Order Summary */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="text-sm text-gray-500 mb-1">Item</div>
                  <div className="font-semibold text-gray-900">{formData.description}</div>
                  <div className="text-sm text-tuggo-mint">
                    {categories.find(c => c.id === formData.category)?.name} • {vehicles.find(v => v.id === formData.vehicle_type)?.name}
                  </div>
                </div>

                {/* Addresses */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full mt-1" />
                    <div>
                      <div className="text-sm text-gray-500">Recolha</div>
                      <div className="text-gray-900">
                        {formData.pickup_address.street}, {formData.pickup_address.number}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formData.pickup_address.city}
                        {formData.pickup_address.floor > 0 && ` • ${formData.pickup_address.floor}º andar`}
                        {!formData.pickup_address.has_elevator && ' (sem elevador)'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-3 bg-red-500 rounded-full mt-1" />
                    <div>
                      <div className="text-sm text-gray-500">Entrega</div>
                      <div className="text-gray-900">
                        {formData.delivery_address.street}, {formData.delivery_address.number}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formData.delivery_address.city}
                        {formData.delivery_address.floor > 0 && ` • ${formData.delivery_address.floor}º andar`}
                        {!formData.delivery_address.has_elevator && ' (sem elevador)'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recipient */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="text-sm text-gray-500 mb-1">Destinatário</div>
                  <div className="font-semibold text-gray-900">{formData.recipient_name}</div>
                  <div className="text-sm text-gray-500">{formData.recipient_phone}</div>
                </div>

                {/* Scheduling */}
                {formData.is_scheduled && formData.scheduled_date && (
                  <div className="bg-tuggo-mint/10 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="w-4 h-4 text-tuggo-mint" />
                      <span className="font-semibold text-gray-900">Entrega Agendada</span>
                    </div>
                    <div className="text-gray-700">
                      {new Date(formData.scheduled_date).toLocaleDateString('pt-PT', {
                        weekday: 'long', day: 'numeric', month: 'long'
                      })}
                      {formData.scheduled_time_slot && ` • ${timeSlots.find(s => s.id === formData.scheduled_time_slot)?.label}`}
                    </div>
                  </div>
                )}

                {/* Price Breakdown */}
                {priceBreakdown && (
                  <div className="bg-white border-2 border-tuggo-mint rounded-xl p-4">
                    <div className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-tuggo-mint" />
                      Resumo do Preço
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Taxa base ({vehicles.find(v => v.id === formData.vehicle_type)?.name})</span>
                        <span>€{priceBreakdown.base_price.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Distância ({priceBreakdown.distance_km} km)</span>
                        <span>€{priceBreakdown.distance_price.toFixed(2)}</span>
                      </div>
                      {priceBreakdown.weight_multiplier > 1 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Ajuste de peso ({weights.find(w => w.id === formData.weight_category)?.name})</span>
                          <span>x{priceBreakdown.weight_multiplier.toFixed(1)}</span>
                        </div>
                      )}
                      {priceBreakdown.floor_price > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Andares (sem elevador)</span>
                          <span>€{priceBreakdown.floor_price.toFixed(2)}</span>
                        </div>
                      )}
                      {(priceBreakdown.extras_breakdown || []).map((extra) => (
                        <div key={`${extra.name}-${extra.price}`} className="flex justify-between">
                          <span className="text-gray-600">{extra.name}</span>
                          <span>€{extra.price.toFixed(2)}</span>
                        </div>
                      ))}
                      {priceBreakdown.breakdown?.scheduled > 0 && (
                        <div className="flex justify-between text-tuggo-mint">
                          <span>Taxa de agendamento</span>
                          <span>€{priceBreakdown.breakdown.scheduled.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="border-t border-gray-200 pt-2 mt-2">
                        <div className="flex justify-between text-lg font-bold">
                          <span>Total</span>
                          <span className="text-tuggo-mint">€{priceBreakdown.total.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-4 mt-6">
                <Button onClick={() => setStep(4)} variant="outline" className="flex-1 rounded-full py-6">
                  Voltar
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 bg-tuggo-mint text-white rounded-full py-6"
                  data-testid="confirm-order"
                >
                  {loading ? 'A criar...' : 'Confirmar Pedido'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
